// @ts-check
import axios from 'axios';
import config from '../constants/config';

const locationFeatureFlagsAxios = axios.create();
const locationFeatureFlagService = {};

/**
 * @typedef {Omit<import('../../../types/common').PaginationOptions, 'fromDate' | 'toDate'> & {
 *   fromDate?: string,
 *   toDate?: string,
 * }} LocationFeatureFlagQuery
 */
/** @typedef {import('../../../types/common').ObjectIdType} ObjectIdType */
/** @typedef {import('../../../types/featureFlag').FeatureFlag} FeatureFlag */
/** @typedef {import('../../../types/locationFeatureFlags').LocationFeatureFlagAction} LocationFeatureFlagAction */
/**
 * @typedef {Object} LookupLocationFeatureFlagInput
 * @property {FeatureFlag} featureFlag
 */
/**
 * @typedef {Object} SaveLocationFeatureFlagInput
 * @property {ObjectIdType} locationId
 * @property {FeatureFlag} featureFlag
 * @property {LocationFeatureFlagAction} action
 */
/**
 * @typedef {Object} SaveLocationFeatureFlagByFeatureFlagInput
 * @property {FeatureFlag} featureFlag
 * @property {LocationFeatureFlagAction} action
 */
/**
 * @typedef {Object} SaveLocationFeatureFlagByCompanyInput
 * @property {ObjectIdType} companyId
 * @property {FeatureFlag} featureFlag
 * @property {LocationFeatureFlagAction} action
 */

/**
 * Get the paginated location feature flag list for this user.
 * @param {LocationFeatureFlagQuery} params
 * @returns
 */
locationFeatureFlagService.getLocationFeatureFlags = (params) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/locations/featureFlags/`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  return locationFeatureFlagsAxios.get(url, { headers, params });
};

/**
 * Save the action for a location feature flag.
 * @param {SaveLocationFeatureFlagInput} input
 * @returns
 */
locationFeatureFlagService.saveLocationFeatureFlag = ({ locationId, featureFlag, action }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/locations/featureFlags/${locationId}`;
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const data = {
    featureFlag,
    action,
  };

  return locationFeatureFlagsAxios.post(url, data, { headers });
};

/**
 * Save the action for a feature flag across every location in a company.
 * @param {SaveLocationFeatureFlagByCompanyInput} input
 * @returns
 */
locationFeatureFlagService.bulkSaveLocationFeatureFlagByCompanyId = ({ companyId, featureFlag, action }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/locations/featureFlags/company/${companyId}`;
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const data = {
    featureFlag,
    action,
  };

  return locationFeatureFlagsAxios.post(url, data, { headers });
};

/**
 * Save the action for a feature flag across every active location.
 * @param {SaveLocationFeatureFlagByFeatureFlagInput} input
 * @returns
 */
locationFeatureFlagService.bulkSaveLocationFeatureFlagByFeatureFlag = ({ featureFlag, action }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/locations/featureFlags/featureFlag/${encodeURIComponent(featureFlag)}`;
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const data = { action };

  return locationFeatureFlagsAxios.post(url, data, { headers });
};

/**
 * Look up the configured action for a feature flag at the current location.
 * @param {LookupLocationFeatureFlagInput} input
 * @returns
 */
locationFeatureFlagService.lookupLocationFeatureFlag = ({ featureFlag }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/locations/featureFlags/${encodeURIComponent(featureFlag)}`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  return locationFeatureFlagsAxios.get(url, { headers });
};

export default locationFeatureFlagService;
